import React, { forwardRef, memo, useMemo } from 'react'
import { Row, Col, Form } from 'react-bootstrap'

/**
 * 汎用入力行
 * - 完全制御: value + onChange を前提（非編集時は表示のみ）
 * - isInvalid/Feedback によるエラースタイル
 * - aria-* を整備してアクセシビリティ向上
 * - React.memo で不要レンダ削減
 */
export const InputItem = memo(
  forwardRef(function InputItem(props, ref) {
    const {
      controlKey,
      label,
      edit = true,
      required = false,
      load = false,
      formType = 'text',         // 'text' | 'email' | 'tel' | 'number' | 'password' | 'textarea' ...
      value = '',
      onChange,
      onBlur,
      help,
      error,
      placeholder,
      rows = 3,                  // textarea の既定行数
      autoComplete,
      className = '',
      // 追加で渡したい属性（input用）
      inputProps = {},
    } = props

    const id = useMemo(() => `form-${controlKey}`, [controlKey])

    const describedBy = useMemo(() => {
      const ids = []
      if (help) ids.push(`${id}-help`)
      if (error) ids.push(`${id}-error`)
      return ids.join(' ') || undefined
    }, [help, error, id])

    const computedPlaceholder = placeholder ?? `${label}を入力`

    // textarea 指定の簡易ハンドリング
    const asTextarea = formType === 'textarea'

    return (
      <Form.Group as={Row} className={`mb-3 ${className}`} controlId={id}>
        <Form.Label column sm={3} className="mb-0">
          {label}
          {(edit && required) && <sup className="text-danger ms-1">*</sup>}
        </Form.Label>

        <Col sm={9}>
          {edit ? (
            <>
              <Form.Control
                ref={ref}
                as={asTextarea ? 'textarea' : 'input'}
                type={asTextarea ? undefined : formType}
                // 完全制御
                value={value ?? ''}
                onChange={onChange}
                onBlur={onBlur}
                disabled={load}
                placeholder={computedPlaceholder}
                rows={asTextarea ? rows : undefined}
                autoComplete={autoComplete}
                isInvalid={Boolean(error)}
                aria-invalid={Boolean(error) || undefined}
                aria-describedby={describedBy}
                required={required}
                {...inputProps}
              />

              {help && (
                <Form.Text id={`${id}-help`} className="text-muted">
                  {help}
                </Form.Text>
              )}

              {Boolean(error) && (
                <Form.Control.Feedback type="invalid" id={`${id}-error`}>
                  {error}
                </Form.Control.Feedback>
              )}
            </>
          ) : (
            <div className="col-form-label d-flex align-items-center justify-content-start">
              {value ?? '—'}
            </div>
          )}
        </Col>
      </Form.Group>
    )
  })
)